/**
 * Shared cockpit "Finding-Reason"-Label resolution (fix cockpit-worklist-amount).
 *
 * Extracted from `FindingDrilldown.tsx` — that was the ONLY caller that
 * correctly interpolated `{{amount}}` into `cockpit.reason.<code>` i18n
 * strings (from `evidence.taxTotal`). `AdvisorCockpitPage.tsx` rendered the
 * SAME reason keys at two other call sites (worklist chip + resolve-list
 * label) WITHOUT the `amount` variable, so the raw `{{amount}} €` template
 * leaked into the always-visible StB-Cockpit-Worklist in prod.
 *
 * IMPORTANT deviation from the naive "just pass evidence everywhere" fix:
 * the worklist-level `CockpitFinding` (`src/api/advisorCockpitApi.ts`,
 * mirroring `functions/src/moat/cockpitRanking.ts`) does NOT carry
 * `evidence` — it is INTENTIONALLY omitted server-side ("kept server-side /
 * in `document_checks` so the list payload stays small and carries no
 * free-text that could read as a legal claim", see `cockpitRanking.ts`
 * `CockpitFinding` doc comment). So {@link resolveReasonLabel}'s `evidence`
 * parameter is OPTIONAL: the two `AdvisorCockpitPage` call sites pass none
 * and always get the clean amount-less fallback sentence (no more raw
 * placeholder); `FindingDrilldown` DOES have `evidence` (from
 * `getCockpitDocumentDetail`) and passes it through for full interpolation,
 * unchanged behaviour from before this fix.
 *
 * @module utils/cockpitReason
 */

import type { TFunction } from 'i18next';

/**
 * de-DE amount (2 decimals, no currency symbol) derived from a finding's
 * `evidence.taxTotal`. The value is already in EURO, not cents (a check that
 * emits `booked_scheme_contradicts_evidence` reports `taxTotal === 57` for
 * 57 €), so it is formatted directly. Returns `null` when the field is
 * absent/non-numeric (incl. when `evidence` itself is absent) so the caller
 * can fall back to an amount-less sentence.
 */
export function findingAmountDe(evidence: Record<string, unknown> | null | undefined): string | null {
  const raw = evidence?.taxTotal;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  try {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(raw);
  } catch {
    return raw.toFixed(2);
  }
}

/**
 * Localised reason label for one cockpit finding, with `{{amount}}`
 * interpolated from `evidence.taxTotal` (Jurist-Auflage / e-rechnung A-2
 * wording) WHEN evidence carrying a numeric `taxTotal` is available. When no
 * amount is present (evidence absent — worklist call sites — or present but
 * without a numeric `taxTotal`) the reason sentence is shown WITHOUT the
 * empty "( €)" parenthetical — the single source of truth for this fallback,
 * used identically by the worklist chip, the resolve-list label, and the
 * drill-down evidence panel.
 *
 * @param t react-i18next translate function (namespace `common`).
 * @param code the finding's `code` (i18n key suffix `cockpit.reason.<code>`).
 * @param evidence the finding's structured evidence, or `undefined`/`null`
 *   when unavailable (worklist-level `CockpitFinding` never carries it).
 */
export function resolveReasonLabel(
  t: TFunction,
  code: string,
  evidence?: Record<string, unknown> | null,
): string {
  const amount = findingAmountDe(evidence);
  // Always pass `amount` — an EMPTY STRING when no numeric taxTotal is available
  // (worklist chip / resolve-list label). Reason (LIVE-Re-Test 2026-07-23): this
  // app's i18next config does NOT drop a missing interpolation variable, it keeps
  // the raw `{{amount}}` placeholder — so `t(key, {})` rendered "({{amount}} €)"
  // and the old empty-only strip below never matched, leaking `{{amount}}` into
  // the always-visible StB-Worklist. Passing '' makes i18next deterministically
  // render "( €)", which the strip then removes.
  const label = t(`cockpit.reason.${code}`, {
    defaultValue: code,
    amount: amount !== null ? amount : '',
  });
  if (amount !== null) return label;
  // Drop the amount parenthetical when no amount was interpolated — matches BOTH
  // the deterministic "( €)" (empty string passed) AND, defensively, a leftover
  // "({{amount}} €)" should any call site reach this without passing `amount`.
  return label
    .replace(/\s*\(\s*(?:\{\{\s*amount\s*\}\})?\s*€\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
